import { inject, Injectable, OnDestroy } from '@angular/core';
import { interval, Subscription } from "rxjs";
import { MatDialog } from "@angular/material/dialog";
import { TimeoutDialogComponent } from "../../../shared/ui/timeout-dialog/timeout-dialog.component";
import { AppMsalAuthService } from "../../auth/authentication-service/app-msal-auth.service";
import { Router } from '@angular/router';
import { MILLISECONDS_PER_SECOND } from '../../constants';

// Don't calculate idle status on these routes
const EXCLUDED_ROUTES: string[] = [
  "logout",
  "login-failed",
  "unauthorised"
];

/**
 * Polls for user activity and logs out if idle for too long.
 * Used this link to implement service:
 * https://dev.to/sarmitha/implement-idle-timeout-in-angular-c94 */
@Injectable({
  providedIn: 'root'
})
export class IdleService implements OnDestroy {
  // Constants, time in seconds.

  readonly TIMEOUT: number = 900;
  readonly TIMEOUT_DIALOG: number = 780;
  readonly IDLE_CHECK_INTERVAL_SECONDS: number = 30;

  // Injectables

  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly authService = inject(AppMsalAuthService);
  private readonly router = inject(Router);

  private lastActivity?: Date;
  private idleSubscription?: Subscription;

  // Lifecycle

  constructor() {
    this.resetTimer();
    this.startWatching();
  }

  ngOnDestroy(): void {
    this.cleanupInterval();
  }

  // Public API

  /**
   * Begin checking if a user has been inactive for longer than a specified timeout.
   */
  public startWatching(): void {
    this.cleanupInterval();

    this.idleSubscription = interval(
      this.IDLE_CHECK_INTERVAL_SECONDS * MILLISECONDS_PER_SECOND // How often a check should occur to see if a user is idle, every IDLE_CHECK_INTERVAL seconds
    )
      .subscribe(() => {
        if (this.isExcludedRoute() || this.lastActivity == undefined) {
          return;
        }

        const now = new Date();
        const idleTimeSeconds = this.secondsElapsed(this.lastActivity, now);

        if (idleTimeSeconds > this.TIMEOUT_DIALOG * MILLISECONDS_PER_SECOND) {
          this.openDialog();
        }

        if (idleTimeSeconds > this.TIMEOUT * MILLISECONDS_PER_SECOND) {
          this.authService.logout();
        }
      });
  }

  /**
   * Resets the lastActivity date to current date.
   */
  public resetTimer(): void {
    this.lastActivity = new Date();
  }

  // Internals

  /**
   * Checks if currently on an excluded route.
   * @returns Route is excluded
   * @private
   */
  private isExcludedRoute(): boolean {
    return EXCLUDED_ROUTES.some(route => this.router.url.includes(route));
  }

  /**
   * Opens a MatDialog if one is not already open.
   * @private
   */
  private openDialog(): void {
    if (this.dialog.openDialogs.length < 1) {
      this.dialog.open(TimeoutDialogComponent);
    }
  }

  /**
   * Returns seconds elapsed between `from` and `to` dates.
   * @param from Start time
   * @param to End time
   * @returns Seconds elapsed
   * @private
   */
  private secondsElapsed(from: Date, to: Date): number {
    return to.getTime() - from.getTime()
  }

  /**
   * Unsubscribes from interval if currently running.
   * @private
   */
  private cleanupInterval(): void {
    if (this.idleSubscription) {
      this.idleSubscription.unsubscribe();
    }
  }
}

